import { Buffer } from 'buffer'
import { store } from 'app/store'
import { Classroom } from 'pages/classroom'
import { Classrooms } from 'pages/classrooms'
import { ComponentStaging } from 'pages/component-staging'
import { Dashboard } from 'pages/dashboard'
import { ForgotPassword } from 'pages/forgot-password'
import { Home } from 'pages/home'
import { Layout, LayoutWithoutReturnToDashboard } from 'pages/layout'
import { LessonBuilder } from 'pages/lesson-builder'
import { Lessons } from 'pages/lessons'
import { LessonOverviewPage } from 'ai-generated/lesson-overview-page'
import { QuestionResultsPage } from 'ai-generated/question-results-page'
import { Login } from 'pages/login'
import { ResetPassword } from 'pages/reset-password'
import NewPasswordRequired from 'pages/new-password-required'
import { Settings } from 'pages/settings'
import { Signup } from 'pages/signup'
import { StudentOverviewPage } from 'pages/student-overview'
import { StudentLessonResultsPage } from 'pages/student-lesson-results'
import { Success } from 'pages/success'
import { UploadLessons } from 'pages/upload-lessons'
import { Verification } from 'pages/verification'
import { EnterCode } from 'pages/enter-code'
import { PresentLessonPage } from 'pages/present-lesson'
import { StudentLivePage } from 'pages/student-live'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { UpdateBanner } from 'components/update-banner'
import { ErrorBoundary } from 'components/error-boundary'
import { ScrollToTop } from 'components/scroll-to-top'
import { ToastHost } from 'components/toast'
import { installGlobalErrorHandlers } from 'util/error-reporting'
import 'css/common.css'
import 'css/fonts.css'
import 'css/print.css'

window.Buffer = window.Buffer ?? Buffer

// Every uncaught error/rejection becomes visible (M-BIG3); must run before render
installGlobalErrorHandlers()

// Instant redirect: students go to /enter-code, teachers go to /dashboard
const HomeRedirect = () => {
	const isStudent = localStorage.getItem('studentId')
	return <Navigate to={isStudent ? '/enter-code' : '/dashboard'} replace />
}

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
	<Provider store={store}>
	<React.StrictMode>
		<BrowserRouter>
			<ScrollToTop />
			<ErrorBoundary label="root">
			<ToastHost />
			<UpdateBanner />
			<Routes>
				<Route path="/" element={<Layout />}>
					<Route path="settings" element={<Settings />} />
					<Route path="lessons" element={<Lessons />} />
					<Route path="upload-lessons" element={<UploadLessons />} />
					<Route path="lesson-builder" element={<LessonBuilder />} />
					<Route path="staging" element={<ComponentStaging />} />
					<Route path='classrooms' element={<Classrooms />} />
					<Route path='classrooms/:classId' element={<Classroom />} />
					<Route path='classrooms/:classId/lessons/:lessonId' element={<LessonOverviewPage />} />
					<Route path='classrooms/:classId/lessons/:lessonId/questions/:questionId' element={<QuestionResultsPage />} />
					<Route path='classrooms/:classId/students/:studentId' element={<StudentOverviewPage />} />
					<Route path='classrooms/:classId/students/:studentId/lessons/:lessonId' element={<StudentLessonResultsPage />} />
				</Route>
				<Route path="/" element={<LayoutWithoutReturnToDashboard />}>
					<Route index element={<HomeRedirect />} />
					<Route path="new-password-required" element={<NewPasswordRequired />} />
					<Route path="dashboard" element={<Dashboard />} />
					<Route path="login" element={<Login />} />
					<Route path="verification" element={<Verification />} />
					<Route path="signup" element={<Signup />} />
					<Route path="success" element={<Success />} />
					<Route path="forgot-password" element={<ForgotPassword />} />
					<Route path="reset-password" element={<ResetPassword />} />
				</Route>
				<Route path="/enter-code" element={<EnterCode />} />
				{/* Live pages get their own boundaries: a render error mid-class
				    must never take down more than the page it happened on */}
				<Route path="/present/:classId/:lessonId" element={<ErrorBoundary label="present-lesson"><PresentLessonPage /></ErrorBoundary>} />
				<Route path="/live/:sessionCode" element={<ErrorBoundary label="student-live"><StudentLivePage /></ErrorBoundary>} />
			</Routes>
			</ErrorBoundary>
		</BrowserRouter>
	</React.StrictMode>
	</Provider>
)
